/**
 * Report Apps — NGXS action set for the report-app framework.
 *
 * Mirrors {@code FilterAppState}'s descriptor lifecycle (bootstrap → refresh →
 * rebind) but swaps the filter-app {@code SearchFilterApp} for {@link RunReport}
 * (which projects the {@code {header,columns,rows}} result JSON instead of
 * bumping matrix reload counters).
 *
 * Action discriminators are {@code [ReportApp] <Name>} so devtools and the
 * action stream stay distinct from {@code [FilterApp]} / {@code [TsmApp]}.
 */

import { ReportAppDescriptor, ReportAppSnapshot, ReportResult } from '../../models/report-app.model';

export class InitReportAppState {
	static readonly type: string = '[ReportApp] InitReportAppState';
	constructor(
		public readonly appName: string,
		public readonly profileCd?: string,
	) {}
}

export class ClearReportAppState {
	static readonly type: string = '[ReportApp] ClearReportAppState';
}

/**
 * Bootstrap the report-app shell via {@code POST ?action=descriptor}. Server
 * mints a {@code formId}, runs the filter-info command to drive the observer
 * chain into a coherent initial state, then projects the descriptor (filter
 * pane + format block). Mirrors {@code BootstrapFilterApp}.
 *
 * <p>{@code autoRun} defaults to TRUE because opening or switching a saved view
 * RUNS its query in Angular (tms-331-profile-system-migration.md §18) — the
 * default belongs here, not at each call site, so a new caller inherits the rule
 * instead of having to remember it. Pass {@code false} only where a run would be
 * wrong: Clear (legacy {@code refreshReportResults} clears the output) and the
 * FORM_EXPIRED recovery (the lifecycle orchestrator replays the original action
 * immediately after, so an auto-run there runs the report twice).
 *
 * <p>The panel's Reset deliberately KEEPS the default even though it looks like
 * Clear's sibling: Clear empties the screen on purpose, while Reset restores the
 * active profile's saved filters — leaving the previous filters' rows on screen
 * (or, since a bootstrap nulls {@code result}, a blank surface) would contradict
 * the chips the user is looking at.
 */
export class BootstrapReportApp {
	static readonly type: string = '[ReportApp] BootstrapReportApp';
	constructor(
		public readonly appName: string,
		public readonly profileCd?: string,
		public readonly autoRun: boolean = true
	) {}
}

/**
 * Filter-field-change refresh — {@code POST ?action=refresh}. Server applies
 * each changed adaptor (triggering the observer chain), then re-projects the
 * descriptor.
 */
export class RefreshReportApp {
	static readonly type: string = '[ReportApp] RefreshReportApp';
	constructor(
		public readonly changedFields: Readonly<Record<string, string | number | boolean | null>>,
		/** Re-dispatch after a FORM_EXPIRED recovery — a second 410 is surfaced, never recovered again. */
		public readonly replay: boolean = false
	) {}
}

/**
 * Run the report — {@code POST ?action=runReport}. Server runs the engine pass
 * and returns the {@code {header,columns,rows}} JSON. The result is stashed in
 * state and rendered by {@code ReportTableRenderer}. When the current format is
 * {@code excel}, the page skips render and triggers a download instead (the
 * page owns that branch, not this action).
 */
/** Hand over the last run's workbook as it is — after an empty run, the headings-only file the user asked for. */
export class DownloadReportWorkbook {
	static readonly type: string = '[ReportApp] DownloadReportWorkbook';
}

/** After an Excel run with records (or a switch to Excel): fetch its workbook as the Workbooks grid's sheets (fire-and-forget). */
export class LoadReportWorkbookPreview {
	static readonly type: string = '[ReportApp] LoadReportWorkbookPreview';
	constructor(public readonly formId: string) {}
}

/** After an empty run: ask the server for the smallest date window with records (fire-and-forget, never blocks a run). */
export class LoadReportRangeSuggestion {
	static readonly type: string = '[ReportApp] LoadReportRangeSuggestion';
	constructor(public readonly formId: string) {}
}

export class RunReport {
	static readonly type: string = '[ReportApp] RunReport';
	/** @param replay re-dispatch after a FORM_EXPIRED recovery — a second 410 is surfaced, never recovered again. */
	constructor(public readonly replay: boolean = false) {}
}

/**
 * Fetch the available-profile list via {@code ?action=profiles}. Fire-and-forget
 * after bootstrap so the profile selector can render groups. Mirrors
 * {@code LoadFilterAppProfiles}.
 */
export class LoadReportAppProfiles {
	static readonly type: string = '[ReportApp] LoadReportAppProfiles';
	constructor(public readonly appName: string) {}
}

/**
 * Re-bind report-app state to a still-cached server-side processor by formId —
 * {@code POST ?action=rebind}. Path B page-state preservation. On 410 the
 * orchestrator falls back to {@link BootstrapReportApp}.
 */
export class RebindReportForm {
	static readonly type: string = '[ReportApp] RebindReportForm';
	constructor(
		public readonly formId: string,
		public readonly profileCd: string | null,
	) {}
}

/** Internal — applies a server descriptor envelope to NGXS state. */
export class ApplyReportDescriptorUpdate {
	static readonly type: string = '[ReportApp] ApplyReportDescriptorUpdate';
	constructor(public readonly descriptor: ReportAppDescriptor) {}
}

/** Internal — stash the run-report result JSON. */
export class ApplyReportResult {
	static readonly type: string = '[ReportApp] ApplyReportResult';
	constructor(public readonly result: ReportResult) {}
}

/**
 * Set the output format ({@code screen} | {@code excel}) on the cached
 * descriptor. The format is a client-side UI choice — how the run is drawn (rows, or its workbook) —
 * so no server round-trip is needed to pick it;
 * the server already seeds {@code format.current} from {@code dump_option} at
 * bootstrap.
 */
export class SetReportFormat {
	static readonly type: string = '[ReportApp] SetReportFormat';
	constructor(public readonly format: string) {}
}

/** Toggle the {@code <trovata-configuration-panel>} (filters dropdown). */
export class ToggleReportFilterPanel {
	static readonly type: string = '[ReportApp] ToggleReportFilterPanel';
	constructor(public readonly open: boolean) {}
}

/**
 * Path B mini-orchestrator. Re-binds via {@link RebindReportForm}; on 410 falls
 * back to {@link BootstrapReportApp} using the snapshot's profile.
 */
export class RestoreReportAppSnapshot {
	static readonly type: string = '[ReportApp] RestoreReportAppSnapshot';
	constructor(public readonly snapshot: ReportAppSnapshot) {}
}
